import React, { useEffect, useState, useContext, useMemo } from "react";
import Equipments from "./Equipments";
import DefaultFee from "./DefaultFee";
import { Stack } from "@mui/material";
import PercentageInput from "./input-elements/PercentageInput";
import CurrencyInput from "./input-elements/CurrencyInput";
import ToggleSwitch from "./input-elements/ToggleSwitch";
import { EquipmentContext } from "../useContext/context";
import { Form } from "react-bootstrap";
import * as Yup from "yup";
import pricingValidation from "../pricing-validation";
import ToggleSwitchContainer from "./input-elements/ToggleSwitchContainer";
import { EMS_OTHER_DEFAULT_FEE } from "../../../../constants";
import ACHPricing from "./ACHPricing";

const flat_rate_validation = pricingValidation.pricingValidation.flat_rate;

const FlatRatePricing = ({
    selectedEquipments,
    emsEquipments,
    setFormDataByKeyValue,
    errors,
    form_data,
    allEquipments,
    removeError,
}) => {
    const [selectedRate, setSelectedRate] = useState("3");
    const { setErrors, pricingDefaults } = useContext(EquipmentContext);
    const [flatRateDefaults, setFlatRateDefaults] = useState(flat_rate_validation);

    // Update flat rate defaults when pricing defaults load
    useEffect(() => {
        if (pricingDefaults && pricingDefaults.flat_rate) {
            setFlatRateDefaults(pricingDefaults.flat_rate);
        }
    }, [pricingDefaults]);

    // Create dynamic validation schema based on current defaults
    const validationSchema = useMemo(() => {
        return Yup.object().shape({
            flat_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Flat Rate is required")
                .min(
                    flatRateDefaults.flat_rate?.minimum || flat_rate_validation.flat_rate.minimum,
                    `Please enter a rate of ${(flatRateDefaults.flat_rate?.minimum || flat_rate_validation.flat_rate.minimum).toFixed(2)}% or higher.`,
                ),

            flat_rate_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("Transaction Fee is required")
                .min(
                    flatRateDefaults.flat_rate_transaction?.minimum || flat_rate_validation.flat_rate_transaction.minimum,
                    `Please enter a fee of $${(flatRateDefaults.flat_rate_transaction?.minimum || flat_rate_validation.flat_rate_transaction.minimum).toFixed(2)} or higher.`,
                ),

            flat_rate_amex: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("AMEX Flat Rate is required")
                .min(
                    flatRateDefaults.flat_rate_amex?.minimum || flat_rate_validation.flat_rate_amex.minimum,
                    `Please enter a rate of ${(flatRateDefaults.flat_rate_amex?.minimum || flat_rate_validation.flat_rate_amex.minimum).toFixed(2)}% or higher.`,
                ),

            flat_rate_amex_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("AMEX Transaction Fee is required")
                .min(
                    flatRateDefaults.flat_rate_amex_transaction?.minimum || flat_rate_validation.flat_rate_amex_transaction.minimum,
                    `Please enter a fee of $${(flatRateDefaults.flat_rate_amex_transaction?.minimum || flat_rate_validation.flat_rate_amex_transaction.minimum).toFixed(2)} or higher.`,
                ),

            flat_rate_pin_debit_rate: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("PIN-based Debit Rate is required")
                .min(
                    flatRateDefaults.flat_rate_pin_debit_rate?.minimum || flat_rate_validation.flat_rate_pin_debit_rate.minimum,
                    `Please enter a rate of ${(flatRateDefaults.flat_rate_pin_debit_rate?.minimum || flat_rate_validation.flat_rate_pin_debit_rate.minimum).toFixed(2)}% or higher.`,
                ),

            flat_rate_pin_debit_per_transaction: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .required("PIN-based Debit Per Transaction fee is required")
                .min(
                    flatRateDefaults.flat_rate_pin_debit_per_transaction?.minimum || flat_rate_validation.flat_rate_pin_debit_per_transaction.minimum,
                    `Please enter a fee of $${(flatRateDefaults.flat_rate_pin_debit_per_transaction?.minimum || flat_rate_validation.flat_rate_pin_debit_per_transaction.minimum).toFixed(2)} or higher.`,
                ),

            fnf_number: Yup.number()
                .transform((value, originalValue) => (originalValue === "" ? 0 : value))
                .when("ebt", {
                    is: "yes",
                    then: Yup.number().required("FNF Number is Required"),
                    otherwise: Yup.number(),
                }),
        });
    }, [flatRateDefaults]);

    const handleOnChange = (e) => {
        try {
            e?.persist();
        } catch (error) {}
        setFormDataByKeyValue(e.target.name, e.target.value);
        validationSchema
            .validateAt(e.target.name, { [e.target.name]: e.target.value })
            .then((e) => {
                // nothing here
            })
            .catch((err) => {
                let validation_errors = errors;
                validation_errors = {
                    ...validation_errors,
                    [e.target.name]: err.errors,
                };
                setErrors(validation_errors);
            });
    };

    return (
        <div className="dual-pricing-section">
            <Stack
                direction={{ xs: "column", sm: "row" }}
                className="container-col-2 mb-4r"
            >
                <div className="top-label-box">
                    <div className="top-label">
                        <img src="/images/visa.png" />
                        <img src="/images/mastercard.png" />
                        <img src="/images/discover.png" />
                    </div>
                    <div className="top-label-box-content">
                        <div className="container-col-2">
                            <div className="w-100">
                                <PercentageInput
                                    label="Flat Rate"
                                    placeholder={flatRateDefaults.flat_rate?.default}
                                    name={"flat_rate"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.flat_rate}
                                    errors={errors}
                                    softLimit={flatRateDefaults?.flat_rate?.soft_limit}
                                />
                                {errors?.flat_rate &&
                                    Array.isArray(errors.flat_rate) &&
                                    errors.flat_rate.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                            <div className="w-100">
                                <CurrencyInput
                                    label="Txn Fee"
                                    placeholder={flatRateDefaults.flat_rate_transaction?.default}
                                    name={"flat_rate_transaction"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.flat_rate_transaction}
                                    errors={errors}
                                    softLimit={flatRateDefaults?.flat_rate_transaction?.soft_limit}
                                />
                                {errors?.flat_rate_transaction &&
                                    Array.isArray(
                                        errors.flat_rate_transaction,
                                    ) &&
                                    errors.flat_rate_transaction.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="top-label-box">
                    <div className="top-label">
                        <img src="/images/amex.png" />
                    </div>
                    <div className="top-label-box-content">
                        <div className="container-col-2">
                            <div className="w-100">
                                <PercentageInput
                                    label="Flat Rate"
                                    placeholder={flatRateDefaults.flat_rate_amex?.default}
                                    name={"flat_rate_amex"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.flat_rate_amex}
                                    errors={errors}
                                    softLimit={flatRateDefaults?.flat_rate_amex?.soft_limit}
                                />
                                {errors?.flat_rate_amex &&
                                    Array.isArray(errors.flat_rate_amex) &&
                                    errors.flat_rate_amex.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                            <div className="w-100">
                                <CurrencyInput
                                    label="Txn Fee"
                                    placeholder={flatRateDefaults.flat_rate_amex_transaction?.default}
                                    name={"flat_rate_amex_transaction"}
                                    type="number"
                                    onChange={handleOnChange}
                                    value={form_data.flat_rate_amex_transaction}
                                    errors={errors}
                                    softLimit={flatRateDefaults?.flat_rate_amex_transaction?.soft_limit}
                                />
                                {errors?.flat_rate_amex_transaction &&
                                    Array.isArray(
                                        errors.flat_rate_amex_transaction,
                                    ) &&
                                    errors.flat_rate_amex_transaction.map(
                                        (error) => (
                                            <span className="error-message">
                                                {error}
                                            </span>
                                        ),
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            </Stack>

            <div className="equipment-box mt-2r">
                <p className="box-label">PIN-based Debit</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="w-60">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) => {
                                setFormDataByKeyValue("flat_rate_pin_debit_network_fee", value);
                                if (value === "no") {
                                    setFormDataByKeyValue("flat_rate_pin_debit_rate", 0);
                                    setFormDataByKeyValue("flat_rate_pin_debit_per_transaction", 0);
                                }
                                if (value === "yes") {
                                    setFormDataByKeyValue("flat_rate_pin_debit_rate", flatRateDefaults.flat_rate_pin_debit_rate?.default ?? 0);
                                    setFormDataByKeyValue("flat_rate_pin_debit_per_transaction", flatRateDefaults.flat_rate_pin_debit_per_transaction?.default ?? 0);
                                }
                            }}
                            value={form_data.flat_rate_pin_debit_network_fee}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="flat_rate_pin_debit_network_fee"
                            label="Network Fee"
                        />
                        {errors?.flat_rate_pin_debit_network_fee &&
                            Array.isArray(
                                errors.flat_rate_pin_debit_network_fee,
                            ) &&
                            errors.flat_rate_pin_debit_network_fee.map(
                                (error) => (
                                    <span className="error-message">
                                        {error}
                                    </span>
                                ),
                            )}
                    </div>
                    <div className="w-100">
                        <PercentageInput
                            label="Debit Rate"
                            type="number"
                            placeholder={flatRateDefaults.flat_rate_pin_debit_rate?.default}
                            name={"flat_rate_pin_debit_rate"}
                            onChange={handleOnChange}
                            value={form_data.flat_rate_pin_debit_rate}
                            errors={errors}
                            softLimit={flatRateDefaults?.flat_rate_pin_debit_rate?.soft_limit}
                            displaySoftLimitErrorMsg={form_data.flat_rate_pin_debit_network_fee}
                        />
                        {errors?.flat_rate_pin_debit_rate &&
                            Array.isArray(errors.flat_rate_pin_debit_rate) &&
                            errors.flat_rate_pin_debit_rate.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                    <div className="w-100">
                        <CurrencyInput
                            label="Per Txn"
                            type="number"
                            placeholder={flatRateDefaults.flat_rate_pin_debit_per_transaction?.default}
                            name={"flat_rate_pin_debit_per_transaction"}
                            onChange={handleOnChange}
                            value={
                                form_data.flat_rate_pin_debit_per_transaction
                            }
                            errors={errors}
                            softLimit={flatRateDefaults?.flat_rate_pin_debit_per_transaction?.soft_limit}
                            displaySoftLimitErrorMsg={form_data.flat_rate_pin_debit_network_fee}
                        />
                        {errors?.flat_rate_pin_debit_per_transaction &&
                            Array.isArray(
                                errors.flat_rate_pin_debit_per_transaction,
                            ) &&
                            errors.flat_rate_pin_debit_per_transaction.map(
                                (error) => (
                                    <span className="error-message">
                                        {error}
                                    </span>
                                ),
                            )}
                    </div>
                </Stack>
            </div>

            <div className="equipment-box mt-2r">
                <p className="box-label">ACH</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="w-100 d-flex">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue("add_ach", value)
                            }
                            value={form_data.add_ach}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="add_ach"
                            label="Add ACH"
                        />
                    </div>
                </Stack>
            </div>

            {form_data.add_ach === "yes" && (
                <ACHPricing
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    errors={errors}
                    form_data={form_data}
                    allEquipments={allEquipments}
                    removeError={removeError}
                    isAddOn={true}
                />
            )}

            <div className="equipment-box mt-2r ebt-transaction">
                <p className="box-label">EBT Transactions</p>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    className="container-col-2 mb-1r mt-2r"
                >
                    <div className="ebt-switch">
                        <ToggleSwitchContainer
                            handleChange={(_e, value) =>
                                setFormDataByKeyValue("ebt", value)
                            }
                            value={form_data.ebt}
                            options={[
                                { key: "yes", value: "Yes" },
                                { key: "no", value: "No" },
                            ]}
                            name="ebt"
                            label="EBT"
                        />
                        {errors?.ebt &&
                            Array.isArray(errors.ebt) &&
                            errors.ebt.map((error) => (
                                <span className="error-message">{error}</span>
                            ))}
                    </div>
                    {form_data.ebt == "yes" && (
                        <div className="d-flex w-100">
                            <div className="floating-form w-100">
                                <Form.Group size="lg" controlId="fnf_number">
                                    <Form.Control
                                        placeholder="FNS Number"
                                        type="text"
                                        name="fnf_number"
                                        value={form_data.fnf_number}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*$/.test(value)) {
                                                handleOnChange(e);
                                            }
                                        }}
                                        className="input-control"
                                    />
                                    <Form.Label className="floating-label">
                                        FNF Number
                                    </Form.Label>
                                </Form.Group>
                                {errors?.fnf_number &&
                                    Array.isArray(errors.fnf_number) &&
                                    errors.fnf_number.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                            <div className="floating-form w-100">
                                <CurrencyInput
                                    label="EBT Transactions"
                                    endAdornment="/txn"
                                    placeholder={
                                        EMS_OTHER_DEFAULT_FEE.ebt_transactions
                                    }
                                    value={form_data.ebt_transactions}
                                    name={"ebt_transactions"}
                                    type="number"
                                    onChange={handleOnChange}
                                    errors={errors}
                                />
                                {errors?.ebt_transactions &&
                                    Array.isArray(errors.ebt_transactions) &&
                                    errors.ebt_transactions.map((error) => (
                                        <span className="error-message">
                                            {error}
                                        </span>
                                    ))}
                            </div>
                        </div>
                    )}
                </Stack>
            </div>

            <DefaultFee
                errors={errors}
                form_data={form_data}
                setFormDataByKeyValue={setFormDataByKeyValue}
                removeError={removeError}
            />
            {selectedRate && (
                <Equipments
                    selectedEquipments={selectedEquipments}
                    emsEquipments={emsEquipments}
                    setFormDataByKeyValue={setFormDataByKeyValue}
                    form_data={form_data}
                    errors={errors}
                    allEquipments={allEquipments}
                    removeError={removeError}
                />
            )}
        </div>
    );
};

export default FlatRatePricing;
